import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import {
  MEETING_FORMATS,
  type MeetingFormat,
  type RecordPrefillSource,
} from "@/features/forms/shared/record/record-prepare";
import {
  isRecordFamilyKey,
  type RecordSubjectKind,
} from "@/features/forms/shared/record/record-family";
import {
  SUSTAINABILITY_VIEWS,
  TIME_HORIZON_MAX_YEARS,
  type SustainabilityView,
} from "@/features/advice-steps/lib/pick";

export interface ChatPrepareSeed {
  clientId: string | null;
  clientQuery: string | null;
  quantPortfolioId: string | null;
  meetingFormat: MeetingFormat | null;
  startMode: PrepareStartMode | null;
  priorRecordId: string | null;
  deposit: number | null;
  withdrawal: number | null;
  buffer: number | null;
  amountDropped: boolean;
  reason: string | null;
  timeHorizon: number | null;
  timeHorizonComment: string | null;
  sustainability: SustainabilityView | null;
}

export interface ChatPrepareClient {
  kind: RecordSubjectKind;
  subjectId: string;
  label: string;
}

export interface ChatPrepareAnswers {
  client: ChatPrepareClient | null;
  quantPortfolioId: string | null;
  portfolioLabel: string | null;
  meetingFormat: MeetingFormat | null;
  startMode: PrepareStartMode | null;
  prior: RecordPrefillSource | null;
  sourceLabel: string | null;
}

export interface ChatPrepare {
  toolCallId: string;
  seed: ChatPrepareSeed;
  answers: ChatPrepareAnswers;
  handedOver: boolean;
}

export type ChatPrepareStage = ChatCardStage;

export type PrepareStartMode = (typeof PREPARE_START_MODES)[number];

export type PrepareStep = (typeof PREPARE_STEPS)[number];

export const PREPARE_START_MODES = ["new", "prior", "pdf"] as const;

export const PREPARE_STEPS = ["client", "portfolio", "meeting", "start"] as const;

export const EMPTY_PREPARE_SEED: ChatPrepareSeed = {
  clientId: null,
  clientQuery: null,
  quantPortfolioId: null,
  meetingFormat: null,
  startMode: null,
  priorRecordId: null,
  deposit: null,
  withdrawal: null,
  buffer: null,
  amountDropped: false,
  reason: null,
  timeHorizon: null,
  timeHorizonComment: null,
  sustainability: null,
};

export const NO_ANSWERS: ChatPrepareAnswers = {
  client: null,
  quantPortfolioId: null,
  portfolioLabel: null,
  meetingFormat: null,
  startMode: null,
  prior: null,
  sourceLabel: null,
};

export function openChatPrepare(toolCallId: string, seed: ChatPrepareSeed): void {
  STORE.open({ toolCallId, seed, answers: NO_ANSWERS, handedOver: false });
}

export function chatPrepareAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function awaitedSteps(seed: ChatPrepareSeed): PrepareStep[] {
  return PREPARE_STEPS.filter((step) => stepAwaited(step, seed));
}

export function optimisesAtOnce({
  deposit,
  withdrawal,
  buffer,
  amountDropped,
}: ChatPrepareSeed): boolean {
  return !amountDropped && (deposit !== null || withdrawal !== null || buffer !== null);
}

export function useChatPrepare(toolCallId: string): ChatPrepare | null {
  return STORE.useEntry(toolCallId);
}

export function useChatPrepareStages(): (toolCallId: string) => ChatPrepareStage | null {
  return STORE.useStages();
}

export function useChatPrepareAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function chatPrepareAnswering(toolCallId: string): boolean {
  return STORE.answering(toolCallId);
}

export function hasChatPrepare(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function answerChatPrepare(toolCallId: string, answers: Partial<ChatPrepareAnswers>): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, answers: { ...entry.answers, ...answers } }));
}

export function chatPrepareHandedOver(toolCallId: string): boolean {
  return STORE.read(toolCallId)?.handedOver === true;
}

export function markChatPrepareHandedOver(
  toolCallId: string,
  settled: Partial<ChatPrepareAnswers>,
): void {
  STORE.update(toolCallId, (entry) => ({
    ...entry,
    answers: { ...entry.answers, ...settled },
    handedOver: true,
  }));
}

function stepAwaited(step: PrepareStep, seed: ChatPrepareSeed): boolean {
  switch (step) {
    case "client":
      return seed.clientId === null;
    case "portfolio":
      return seed.quantPortfolioId === null;
    case "meeting":
      return seed.meetingFormat === null;
    case "start":
      return (
        seed.startMode === null ||
        seed.startMode === "pdf" ||
        (seed.startMode === "prior" && seed.priorRecordId === null)
      );
    default: {
      const unhandled: never = step;
      return unhandled;
    }
  }
}

function narrowEntry(value: unknown): ChatPrepare | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string" || value.toolCallId === "") {
    return null;
  }
  const seed = isRecord(value.seed) ? value.seed : {};
  const answers = isRecord(value.answers) ? value.answers : {};
  return {
    toolCallId: value.toolCallId,
    seed: {
      clientId: text(seed.clientId),
      clientQuery: text(seed.clientQuery),
      quantPortfolioId: text(seed.quantPortfolioId),
      meetingFormat: oneOf(MEETING_FORMATS, seed.meetingFormat),
      startMode: oneOf(PREPARE_START_MODES, seed.startMode),
      priorRecordId: text(seed.priorRecordId),
      deposit: kronor(seed.deposit),
      withdrawal: kronor(seed.withdrawal),
      buffer: kronor(seed.buffer),
      amountDropped: seed.amountDropped === true,
      reason: text(seed.reason),
      timeHorizon: horizon(seed.timeHorizon),
      timeHorizonComment: text(seed.timeHorizonComment),
      sustainability: oneOf(SUSTAINABILITY_VIEWS, seed.sustainability),
    },
    answers: {
      client: client(answers.client),
      quantPortfolioId: text(answers.quantPortfolioId),
      portfolioLabel: text(answers.portfolioLabel),
      meetingFormat: oneOf(MEETING_FORMATS, answers.meetingFormat),
      startMode: oneOf(PREPARE_START_MODES, answers.startMode),
      prior: prior(answers.prior),
      sourceLabel: text(answers.sourceLabel),
    },
    handedOver: value.handedOver === true,
  };
}

function client(value: unknown): ChatPrepareClient | null {
  if (!isRecord(value)) return null;
  const { kind, subjectId, label } = value;
  if (kind !== "individual" && kind !== "corporation") return null;
  if (typeof subjectId !== "string" || subjectId === "" || typeof label !== "string") return null;
  return { kind, subjectId, label };
}

function prior(value: unknown): RecordPrefillSource | null {
  if (!isRecord(value)) return null;
  const { family, id } = value;
  return isRecordFamilyKey(family) && typeof id === "string" && id !== "" ? { family, id } : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function oneOf<V extends string>(values: readonly V[], value: unknown): V | null {
  return values.find((candidate) => candidate === value) ?? null;
}

function kronor(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function horizon(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= TIME_HORIZON_MAX_YEARS
    ? value
    : null;
}

const STORE = toolCallStore<ChatPrepare>({
  key: "simon.advisory.chatPrepare",
  narrow: narrowEntry,
  stage: (entry) => (entry.handedOver ? "done" : "awaiting"),
});
